import { Box, TextField } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";

import appService from "../../../service/app.service";
import KsbBoxHeader from "../../../components/KsbBoxHeader";
import UserCard from "./UserCard";
import UserDialog from "./UserDialog";

function UserView() {
  const theme = useTheme();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");

  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);

  /**
   * ✅ LOAD USERS
   */
  useEffect(() => {
    const loadUsers = async () => {
      const data = await appService.getUsers();
      setUsers(data);
      setFilteredUsers(data);
    };

    loadUsers();
  }, []);

  /**
   * ✅ FILTER (search)
   */
  useEffect(() => {
    const filtered = users.filter((user) => {
      const s = search.toLowerCase();

      const matchesSearch =
        user.firstName?.toLowerCase().includes(s) ||
        user.lastName?.toLowerCase().includes(s) ||
        user.email?.toLowerCase().includes(s) ||
        user.abbreviation?.toLowerCase().includes(s);

      const matchesRole = !roleFilter || user.roles.includes(roleFilter);

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "active" && user.isActive) ||
        (statusFilter === "verified" && user.isVerified && !user.isActive) ||
        (statusFilter === "inactive" && !user.isActive);

      return matchesSearch && matchesRole && matchesStatus;
    });

    setFilteredUsers(filtered);
  }, [search, roleFilter, statusFilter, users]);

  /**
   * ✅ STYLE (same pattern as AccountProfile)
   */

  const sectionStyle = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    p: 3,
    boxShadow: 2,
    maxHeight: "60dvh", // ✅ or "60vh" (better!)
    display: "flex",
    flexDirection: "column",
  };

  const fieldProps = {
    size: "small",
    variant: "outlined",
  };

  return (
    <Box sx={sectionStyle}>
      <KsbBoxHeader title="User Übersicht" />

      {/* ✅ SEARCH */}
      <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "center" }}>
        <TextField
          {...fieldProps}
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          {...fieldProps}
          select
          label="Role"
          size="small"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">All</option>
          <option value="Admin">Admin</option>
          <option value="Teacher">Teacher</option>
          <option value="Student">Student</option>
        </TextField>

        <TextField
          {...fieldProps}
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Find User...</option>
          <option value="active">is active</option>
          <option value="verified">is only verified</option>
          <option value="inactive">is only enabled</option>
        </TextField>
      </Box>

      {/* ✅ USER LIST */}

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflowY: "auto", // ✅ scroll here
          pr: 1, // ✅ prevents scrollbar overlap
        }}
      >
        {filteredUsers.map((user) => (
          <UserCard key={user.id} user={user} onClick={setSelectedUser} />
        ))}
      </Box>

      {selectedUser && (
        <UserDialog
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSave={async (updatedUser) => {
            try {
              // ✅ 1. update backend
              await appService.updateUser(updatedUser.id, updatedUser);

              // ✅ 2. update local state
              setUsers((prev) =>
                prev.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
              );

              // ✅ 3. close dialog
              setSelectedUser(null);
            } catch (error) {
              console.error("❌ Failed to update user:", error);
            }
          }}
        />
      )}
    </Box>
  );
}

export default memo(UserView);
